import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Mail, Lock, Eye, EyeOff, Loader2, User, Building2, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const Register = () => {
  const { register, googleLogin, user } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', confirmPassword: '',
    role: 'client', phone: '', company: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (user) { navigate('/dashboard'); return null; }

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (formData.password !== formData.confirmPassword) { setError('Passwords do not match'); return; }
    if (formData.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await register({ name: formData.name, email: formData.email, password: formData.password,
        role: formData.role, phone: formData.phone || null, company: formData.company || null });
      navigate('/dashboard');
    } catch (err) { setError(err.response?.data?.detail || 'Registration failed'); }
    finally { setLoading(false); }
  };

  const roles = [
    { value: 'client', label: 'Director / Producer / Agency' },
    { value: 'talent', label: 'Talent / Actor / Model' },
    { value: 'casting_manager', label: 'Casting Manager' }
  ];

  return (
    <div className="flex" style={{ background: 'var(--ink)', minHeight: '100vh' }}>
      {/* Left Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 z-10" style={{ background: 'linear-gradient(to right, transparent 0%, var(--ink) 100%)' }} />
        <img src="https://images.unsplash.com/photo-1574867022210-bd9ecc413bf3?w=1200&q=80" alt="Talent" className="w-full h-full object-cover" />
        <div className="absolute bottom-12 left-12 right-12 z-20">
          <div className="font-display text-4xl mb-4" style={{ color: 'var(--cream)', letterSpacing: 3 }}>Join India's Premier Casting Platform</div>
          <p className="text-sm" style={{ color: 'var(--warm)', lineHeight: 1.7 }}>
            Whether you're a production house looking for talent or an artist seeking opportunities, CastDeck connects you with the right people.
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-12" data-testid="logo-link">
            <div className="w-7 h-7 bg-[var(--gold)] flex items-center justify-center" style={{ clipPath: 'polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%)' }}>
              <span style={{ fontFamily: 'var(--display)', fontSize: 14, color: '#000', letterSpacing: 2 }}>C</span>
            </div>
            <span className="font-display text-2xl" style={{ color: 'var(--cream)', letterSpacing: 3 }}>CASTDECK</span>
          </Link>

          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'var(--head)', color: 'var(--cream)' }}>Create Account</h1>
          <p className="text-sm mb-8" style={{ color: 'var(--warm)' }}>Get started with your casting journey</p>

          {error && (
            <div className="px-4 py-3 mb-6 text-sm" style={{ background: 'rgba(224,86,86,.1)', border: '1px solid rgba(224,86,86,.2)', color: 'var(--red)' }} data-testid="error-message">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-[10px] font-bold tracking-wider uppercase mb-2 block" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>Full Name</Label>
              <Input type="text" value={formData.name} onChange={(e) => handleChange('name', e.target.value)}
                className="h-10" style={{ background: 'var(--surface)', borderColor: 'rgba(255,255,255,.08)', color: 'var(--cream)' }}
                placeholder="Enter your full name" required data-testid="name-input" />
            </div>

            <div>
              <Label className="text-[10px] font-bold tracking-wider uppercase mb-2 block" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>Email</Label>
              <Input type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)}
                className="h-10" style={{ background: 'var(--surface)', borderColor: 'rgba(255,255,255,.08)', color: 'var(--cream)' }}
                placeholder="Enter your email" required data-testid="email-input" />
            </div>

            <div>
              <Label className="text-[10px] font-bold tracking-wider uppercase mb-2 block" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>I am a...</Label>
              <Select value={formData.role} onValueChange={(value) => handleChange('role', value)}>
                <SelectTrigger className="h-10" style={{ background: 'var(--surface)', borderColor: 'rgba(255,255,255,.08)' }} data-testid="role-select">
                  <SelectValue placeholder="Select your role" />
                </SelectTrigger>
                <SelectContent>{roles.map(role => <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] font-bold tracking-wider uppercase mb-2 block" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>Phone (Optional)</Label>
                <Input type="tel" value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)}
                  className="h-10" style={{ background: 'var(--surface)', borderColor: 'rgba(255,255,255,.08)', color: 'var(--cream)' }}
                  placeholder="+91..." data-testid="phone-input" />
              </div>
              <div>
                <Label className="text-[10px] font-bold tracking-wider uppercase mb-2 block" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>Company (Optional)</Label>
                <Input type="text" value={formData.company} onChange={(e) => handleChange('company', e.target.value)}
                  className="h-10" style={{ background: 'var(--surface)', borderColor: 'rgba(255,255,255,.08)', color: 'var(--cream)' }}
                  placeholder="Company name" data-testid="company-input" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] font-bold tracking-wider uppercase mb-2 block" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>Password</Label>
                <div className="relative">
                  <Input type={showPassword ? 'text' : 'password'} value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    className="h-10 pr-10" style={{ background: 'var(--surface)', borderColor: 'rgba(255,255,255,.08)', color: 'var(--cream)' }}
                    placeholder="Min 6 characters" required data-testid="password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-c)' }}>
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-[10px] font-bold tracking-wider uppercase mb-2 block" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>Confirm</Label>
                <Input type={showPassword ? 'text' : 'password'} value={formData.confirmPassword}
                  onChange={(e) => handleChange('confirmPassword', e.target.value)}
                  className="h-10" style={{ background: 'var(--surface)', borderColor: 'rgba(255,255,255,.08)', color: 'var(--cream)' }}
                  placeholder="Re-enter" required data-testid="confirm-password-input" />
              </div>
            </div>

            <button type="submit" className="btn-gold w-full h-11 text-xs flex items-center justify-center" disabled={loading} data-testid="register-btn">
              {loading ? <Loader2 className="w-5 h-5 spinner" /> : 'CREATE ACCOUNT'}
            </button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full" style={{ borderTop: '1px solid rgba(255,255,255,.06)' }} /></div>
            <div className="relative flex justify-center text-[10px] font-bold tracking-wider uppercase" style={{ fontFamily: 'var(--head)' }}>
              <span className="px-4" style={{ background: 'var(--ink)', color: 'var(--muted-c)' }}>Or continue with</span>
            </div>
          </div>

          <button type="button" onClick={googleLogin}
            className="btn-ghost w-full h-11 flex items-center justify-center gap-2 text-xs" data-testid="google-register-btn">
            <svg className="w-4 h-4" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          <p className="text-center mt-6 text-xs" style={{ color: 'var(--warm)' }}>
            Already have an account? <Link to="/login" style={{ color: 'var(--gold)' }} data-testid="login-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
